// ============================================================
// dev-core-web/src/hooks/useToast.ts
// Version: 1.1.0
// Purpose:
//   - React-обёртка над store/uiStore.ts для тостов
//   - предоставляет стабильный API: showToast / dismissToast / clear
//   - НЕ хранит очередь в локальном состоянии — источник истины
//     в uiStore
//   - НЕ логирует содержимое тостов (SECURITY.md §9.3)
//   - toasts — UX, не security evidence
//
// Правила:
//   - showToast принимает ShowToastInput из uiStore
//   - стабильные ссылки (useCallback) — чтобы не ре-рендерить
//     компоненты, использующие хук
//   - никаких секретов/classified values в title/description
//     (CLASSIFICATION.md §14)
//
// Ссылки:
//   - dev-core-web/src/store/uiStore.ts
//   - docs/DEVELOPMENT.md §11.2, §11.5
//   - docs/SECURITY.md §9.3
//   - docs/CLASSIFICATION.md §14
//   - docs/MONITORING.md §20
// ============================================================

import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useUiStore,
  selectToasts,
  type ShowToastInput,
  type Toast,
  type ToastVariant,
} from '@/store/uiStore';

// ------------------------------------------------------------
// Публичный API
// ------------------------------------------------------------

export interface UseToastResult {
  /**
   * Показать toast. Возвращает id — вызывающий может использовать
   * его для последующего dismiss или логической замены.
   */
  showToast: (input: ShowToastInput) => string;
  /** Скрыть конкретный toast по id. */
  dismissToast: (id: string) => void;
  /** Очистить все тосты. */
  clearToasts: () => void;
  /** Текущая очередь (read-only). Обычно рендерит ToastContainer. */
  toasts: Toast[];
  /** Счётчик тостов — удобно для мелких условий. */
  count: number;
}

export function useToast(): UseToastResult {
  // Выбираем из store только то, что нужно.
  // useShallow гарантирует, что компонент не ре-рендерится
  // при изменении несвязанных полей store.
  const toasts = useUiStore(useShallow(selectToasts));

  // Стабильные ссылки на actions из store.
  // Zustand возвращает одни и те же функции между рендерами,
  // но заворачиваем в useCallback для явности контракта.
  const showToastRaw = useUiStore((s) => s.showToast);
  const dismissToastRaw = useUiStore((s) => s.dismissToast);
  const clearToastsRaw = useUiStore((s) => s.clearToasts);

  const showToast = useCallback(
    (input: ShowToastInput): string => showToastRaw(input),
    [showToastRaw],
  );

  const dismissToast = useCallback(
    (id: string): void => dismissToastRaw(id),
    [dismissToastRaw],
  );

  const clearToasts = useCallback((): void => {
    clearToastsRaw();
  }, [clearToastsRaw]);

  return useMemo(
    () => ({
      showToast,
      dismissToast,
      clearToasts,
      toasts,
      count: toasts.length,
    }),
    [showToast, dismissToast, clearToasts, toasts],
  );
}

// ------------------------------------------------------------
// Удобные шорткаты (опционально; страницы их не требуют,
// но они часто нужны в коде).
// ------------------------------------------------------------
//
// Все они используют useToast изнутри — единая точка входа.
// Никаких отдельных side-effect'ов или глобального состояния.

export interface ToastShortcuts {
  info: (title: string, description?: string) => string;
  success: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
}

export function useToastShortcuts(): ToastShortcuts {
  const { showToast } = useToast();

  return useMemo(
    () => ({
      info: (title: string, description?: string) =>
        showToast({ variant: 'info', title, description }),
      success: (title: string, description?: string) =>
        showToast({ variant: 'success', title, description }),
      warning: (title: string, description?: string) =>
        showToast({ variant: 'warning', title, description }),
      error: (title: string, description?: string) =>
        showToast({ variant: 'error', title, description }),
    }),
    [showToast],
  );
}

// ------------------------------------------------------------
// Re-export типов для удобства потребителей
// ------------------------------------------------------------
//
// Страницы импортируют `useToast` и (при необходимости) типы
// тостов из одного места — '@/hooks/useToast'.

export type { ShowToastInput, Toast, ToastVariant };

// ------------------------------------------------------------
// Замечания по интеграции
// ------------------------------------------------------------
//
// 1. Рендеринг тостов.
//    ToastContainer (компонент) подписывается на toasts через
//    useToast().toasts и рендерит их. ToastContainer добавляется
//    в Layout.tsx или App.tsx один раз на верхнем уровне.
//
// 2. Автоудаление по TTL.
//    Логика TTL реализована в store/uiStore.ts::showToast
//    (setTimeout по ttlMs). Хук не дублирует её.
//
// 3. Никаких secrets.
//    title/description — plain-текст для пользователя. Не
//    помещайте в них токены, PII, classified detail.
//    Если нужна классифицированная деталь — она уже должна
//    быть masked сервером (CLASSIFICATION.md §7.2).
//
// 4. Никаких console.log.
//    Хук не пишет в консоль. Если нужны диагностические
//    сообщения — они идут через logger/observability
//    (MONITORING.md), не через toast.